import { useRef, useEffect, useMemo, useCallback } from "react"
import { useField, useFormikContext, getIn, FieldArray } from "formik"

import clsx from "clsx"
import { FileUploader, FileUploaderResponse } from "./FileUploader";
import { isPopulatedArray, number_format, parseTimestamp } from "../helpers";
import { DateSelector } from "./DateSelector";

import { format } from 'date-fns';

import { KTSVG } from "../../../_metronic/helpers";
import { cloneDeep, pick, pickBy, uniq } from "lodash";
import { IWebFileAttachment } from "../../modules/core/applications";
import { AttachmentCard } from "../../modules/core/attachments/AttachmentCard";
import { useAuth } from "../../modules/auth";

const FormikTextInput = ({...props }) => {

  const { label, required, name, size, decimals, onChange: onChangeFunc, title, addClass, alwaysShowError } = props;
  //@ts-ignore
  const [field, meta] = useField(props);
  const { isSubmitting, setFieldValue } = useFormikContext();

  const timerRef = useRef<any>();

  const { value, error } = meta;

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined
    }
  }

  useEffect(() => {
    
    if (value && decimals !== undefined && parseInt(`${decimals}`) >= 0) {
      clearTimer();
      timerRef.current = setTimeout(() => {
        setFieldValue(name, number_format(value, parseInt(`${decimals}`), '.',''), true)
      }, 1200);            
    }

    return(() => {
      clearTimer();            
    })
  // eslint-disable-next-line
  }, [name, value, error, decimals])

  return (
    <div className={`col-${size}`}>
      {label &&
      <label className={clsx('fw-bold fs-7', { 'required': required === true})}>{label} :</label>
      }
      { (props?.readOnly) ? (
      <input
        className={clsx(`form-control form-control-outline fw-bold fs-7 ${addClass}`,
          {"mb-2 mb-lg-0 py-2" : label },
          {"py-1" : ! label}
        )}
        name={name}
        value={value}
        title={title ? title : ''}
        readOnly
      />
      ) : (
      <>
      <input
        {
          ...pickBy(props, (value, key) => (value && ['type','placeholder','pattern'].includes(key)))
        }
        {...field}
        title={title ? title : ''}
        className={clsx(
          `form-control form-control-solid fw-bold fs-7 ${addClass}`,
          {"mb-2 mb-lg-0 py-2" : label },
          {"py-1" : ! label},
          {'is-invalid': meta.touched && meta.error},
          {'is-valid': meta.touched && !meta.error, }
        )}
        name={name}
        autoComplete={props?.type && props.type === 'password' ? 'new-password' : 'off'}
        disabled={isSubmitting}
        onChange={ (e: any) => {
          setFieldValue(name, e.target.value, true);
          if (onChangeFunc) {
            onChangeFunc(e);
          }
        }}
      />
      </>
      )}
      {(meta.touched || alwaysShowError) && meta.error && (
      <div className='fv-plugins-message-container'>
        <div className='fv-help-block fs-7'>
          <span role='alert'>{meta.error}</span>
        </div>
      </div>
      )}
    </div>
  )
}


const FormikTextField = ({...props }) => {

  const { label, required, name, size, rows, onChange: onChangeFunc } = props;
  //@ts-ignore
  const [field, meta] = useField(props);
  const { isSubmitting, setFieldValue } = useFormikContext();

  return (
  <div className={`col-${size}`}>
    {(label) && 
    <label className={clsx('fw-bold fs-7', { 'required': required === true})}>{label} :</label>
    }
    <textarea
      {...field}
      className={clsx(
        'form-control form-control-sm form-control-solid mb-1 py-2 fw-bold fs-7',
        {'is-invalid': meta.touched && meta.error},
        {'is-valid': meta.touched && !meta.error}
      )}
      name={name}
      autoComplete='off'
      rows={rows && rows > 0 ? rows : 3}
      disabled={isSubmitting}
      onChange={ (e: any) => {
        setFieldValue(name, e.target.value, true);
        if (onChangeFunc) {
            onChangeFunc(e);
        }
      }}
    />

    {meta.touched && meta.error && (
    <div className='fv-plugins-message-container'>
      <div className='fv-help-block fs-7'>
        <span role='alert'>{meta.error}</span>
      </div>
    </div>
    )}
  </div>
  )
}

export type SelectOption = {
  value: string | number
  text: string
}

const FormikSelect = ({...props }) => {

  const { label, required, name, size, options, placeholder, onChange: onChangeFunc, disabled } = props;
  //@ts-ignore
  const [field, meta, helpers] = useField(props);
  const { setValue } = helpers;
  const { isSubmitting } = useFormikContext();

  return (
  <div className={`col-${size}`}>
    { (label !== undefined && label.length > 0) &&
    <label className={clsx('fw-bold fs-7', { 'required': required === true})}>{label} :</label>
    }
    <div className='d-flex fv-row'>
      <select
        {...field}
        className='form-select form-select-solid fs-7 mb-2 py-2 fw-bold'
        name={name}
        disabled={isSubmitting || disabled}
        onChange={(e: any) => {
          setValue(e.target.value, true);
          if (onChangeFunc) {
            onChangeFunc(e);
          }
        }}
      >
        <option value='' className="text-muted">{placeholder}</option>
        {(isPopulatedArray(options)) && options.map((dat: any, idx: number) => (
        <option key={`option${idx}`} value={dat.value}>{dat.text}</option>
        ))}
      </select>
    </div>

    {meta.touched && meta.error && (
    <div className='fv-plugins-message-container'>
      <div className='fv-help-block fs-7'>
        <span role='alert'>{meta.error}</span>
      </div>
    </div>
    )}
  </div>
)
}

const FormikFileUpload = ({...props }) => {

  const { currentUser } = useAuth();

  const { label, required, name, size, isFileArray, onUpdateFunc, disabled, subTitle, subTitleClass } = props;
  //@ts-ignore
  const [, meta] = useField(props);
  const { values, setFieldValue, validateField } = useFormikContext();

  const { fldName, fileName} = useMemo(() => {
  if (isFileArray === true) {
    return { fldName: '', fileName : ""};
  }
  const fldName = name + 'AttachmentName'
  const fileName = getIn(values, fldName);

  return {fldName, fileName};

  }, [name, values, isFileArray]);

  return (
  <div className={`col-${size} mt-2`}>
    <FileUploader
      required={required === true}
      mainTitle={label}
      subTitle={ fileName ? fileName : (subTitle ? subTitle : "Click or drop here to upload.")}
      subTitleClass={subTitleClass}
      fileName={ fileName }
      allowMultiple={false}
      acceptFiles={ (currentUser && currentUser.isSetsco)? 'UserFileFormats' : 'ApplicantFileFormats'}
      onFileUploaded={ (dat: FileUploaderResponse) => {
        if (isFileArray === true) {
          let _files = getIn(values, name);
          if (! _files) _files = [];
          _files.push(dat);
          setFieldValue(name, _files, false);
        } else {
          setFieldValue(name, dat.id, false);
          setFieldValue(fldName, dat.name, false);
        }
        setTimeout(() => {
          validateField(name);
          if (onUpdateFunc) {
            onUpdateFunc();
          }
        }, 100)
      }}
      onClearFile={ () => {
        if (isFileArray === true) {
          setFieldValue(name, [], false);
        } else {
          setFieldValue(name, 0, false);
          setFieldValue(fldName, '', false);
        }
        setTimeout(() => {
          validateField(name);
          if (onUpdateFunc) {
            onUpdateFunc();
          }
        }, 100)
      }}
      disabled={disabled}
    />
    {meta.touched && meta.error && (
    <div className='fv-plugins-message-container'>
      <div className='fv-help-block fs-7'>
        <span role='alert'>{meta.error}</span>
      </div>
    </div>
    )}
  </div>
  )
}

const FormikMultiFileUpload = ({...props }) => {

  const { currentUser } = useAuth();

  const { label, required, name, size, onUpdateFunc, disabled, subTitle } = props;
  //@ts-ignore
  const [, meta] = useField(props);
  const { value } = meta;
  const { setFieldValue, validateField } = useFormikContext();

  return (
  <>
  <div className={`col-${size} mt-2`}>
    <div className="row">
      <div className="col-md-6">
        <FileUploader
          required={required === true}
          mainTitle={label}
          subTitle={(subTitle ? subTitle : "Click or drop here to upload.")}
          allowMultiple={false}
          acceptFiles={ (currentUser && currentUser.isSetsco)? 'UserFileFormats' : 'ApplicantFileFormats'} 
          onFileUploaded={ (dat: FileUploaderResponse) => {
            let _files = cloneDeep(value);
            if (! _files) _files = [];
            _files.push(dat);
            setFieldValue(name, _files, false);
            setTimeout(() => {
              validateField(name);
              if (onUpdateFunc) {
                onUpdateFunc();
              }
            }, 100)
          }}
          onClearFile={ () => {
            setFieldValue(name, [], false);
            setTimeout(() => {
              validateField(name);
              if (onUpdateFunc) {
                onUpdateFunc();
              }
            }, 100)
          }}
          disabled={disabled}
        />
      </div>
    </div>        
    <div className="row">
      { (value && isPopulatedArray(value as Array<IWebFileAttachment>)) &&
      value.map( (o: IWebFileAttachment, idx: number) => (
      <div className="col-6" key={`multifile${idx}`}>
        <AttachmentCard          
          idx={idx}
          fileId={o.id}
          fileName={o.name}
          onRemove={(e: any) => {
            const elem = e.target.closest('.attachment-card');
            if (elem) {
              const {idx} = elem.dataset;
              let _files = cloneDeep(value);
              _files.splice(idx, 1);
              setFieldValue( name, _files);
              setTimeout(() => validateField(name), 100)
            }
          }}
        />
      </div>
      ))
      }
    </div>
    {meta.touched && meta.error && (
    <div className='fv-plugins-message-container'>
      <div className='fv-help-block fs-7'>
        <span role='alert'>{meta.error}</span>
      </div>
    </div>
    )}
  </div>
  </> 
  )
}


const FormikDateSelector = ({...props }) => {

  const { label, required, name, size } = props;
  //@ts-ignore
  const [, meta] = useField(props);
  const { setFieldValue, validateField } = useFormikContext();

  const { value } = meta;

  const selected_dt = useMemo(() => {
    if (! value) {
      return null;
    }
    return parseTimestamp(value);

  }, [value]);

  const onChangeDate =  useCallback((dt: any) => {
    setFieldValue(name, dt ? format(dt, "yyyy-MM-dd'T'HH:mm:ssxx") : '');
    setTimeout(() => validateField(name), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name])

  return (
  <div className={`col-${size}`}>
    { label &&
    <label className={clsx('fw-bold fs-7', { 'required': required === true})}>{label} :</label>
    }
    <DateSelector
      className='form-control form-control-sm form-control-solid mb-2 py-2 fs-7 fw-bold'
      selected={selected_dt}
      onChange={onChangeDate}
      {
        ...pick(props, ['showClock','dateFormat','showShiftSelector','asRow'])
      }
    ></DateSelector>
    {meta.touched && meta.error && (
    <div className='fv-plugins-message-container'>
      <div className='fv-help-block fs-7'>
        <span role='alert'>{meta.error}</span>
      </div>
    </div>
    )}
  </div>
  )
}


const FormikSwitch = ({...props }) => {

  const { label, required, name, size, onChange: onChangeFunc } = props;

  //@ts-ignore
  const [field, meta] = useField(props);
  const { isSubmitting, setFieldValue } = useFormikContext();

  const checkboxRef = useRef<HTMLInputElement>(null);

  const { value } = meta;

  useEffect(() => {
    if (! checkboxRef.current)  return;

    if (checkboxRef.current.checked !== value) {
      checkboxRef.current.checked = value;
    }
  }, [value])


  return (
    <div className={`col-${size}`}>
      {label &&
      <label className={clsx('fw-bold fs-7', { 'required': required === true})}>{label} :</label>
      }
      <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid mt-2'>
        <input
          {...field}
          ref={checkboxRef}
          className='form-check-input'
          type='checkbox'
          onChange={() => {
            const _flagval = checkboxRef.current && checkboxRef.current.checked;
            setFieldValue(name, _flagval);
            if (onChangeFunc) {
              onChangeFunc(_flagval)
            }
          }}
          disabled={isSubmitting}
        />
        <label className='form-check-label'>{ (checkboxRef.current && checkboxRef.current.checked) ? 'YES' : 'NO'}</label>
      </div>

      {meta.touched && meta.error && (
      <div className='fv-plugins-message-container'>
        <div className='fv-help-block fs-7'>
          <span role='alert'>{meta.error}</span>
        </div>
      </div>
      )}
    </div>
  )
}

const FormikRadio = ({...props }) => {

  const { label, name, size, value } = props;

  //@ts-ignore
  // const [field, meta] = useField(props);
  const { isSubmitting, setFieldValue } = useFormikContext();

  return (
    <div className={`col-${size}`}>
      <label className='d-flex flex-row gap-6 mb-3 cursor-pointer'>
        <span className='form-check form-check-custom form-check-solid'>
          <input
            className='form-check-input prodtype-radio'
            type='radio'
            name={name}
            value={value}
            onChange={(e: any) => { setFieldValue(name, e.target.value)}}
            disabled={isSubmitting}
          />
        </span>
        <span className='fw-bolder text-gray-800 text-hover-primary fs-5'>
          {label ? label : value}
        </span>
      </label>
    </div>
  )
}

const FormikCheckBox = ({...props }) => {

  const { label, required, name, size, onChange: onChangeFunc, readOnly, disabled, className } = props;

  //@ts-ignore
  const [field, meta] = useField(props);
  const { isSubmitting, setFieldValue } = useFormikContext();

  const checkboxRef = useRef<HTMLInputElement>(null);

  const { value } = meta;

  useEffect(() => {
    if (! checkboxRef.current)  return;

    if (checkboxRef.current.checked !== value) {
      checkboxRef.current.checked = value;
    }
  }, [value])


  return (
  <div className={`col-${size} ${className ? className : ''}`}>
    <div className='form-check form-check-sm form-check-custom form-check-solid mt-2 d-flex flex-row align-items-start gap-2'>
      <input
        {...field}
        ref={checkboxRef}
        className='form-check-input'
        type='checkbox'
        onChange={() => {
          const _flagval = checkboxRef.current && checkboxRef.current.checked;
          setFieldValue(name, _flagval);
          if (onChangeFunc) {
            onChangeFunc(_flagval)
          }
        }}
        disabled={isSubmitting || disabled}
        readOnly={readOnly}
      />
      <div>
      {label &&
        <label className={clsx('form-check-label fw-bold fs-7', { 'required': required === true})}>{label}</label>
      }
      {meta.touched && meta.error && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block fs-7'>
            <span role='alert'>{meta.error}</span>
          </div>
        </div>
      )}
      </div>
    </div>
  </div>
  )
}


const FormikTagInput =  ({...props }) => {

	const { name: fieldName, size, required, label, subLabel } = props;
  //@ts-ignore
  const [ , meta ] = useField(props);
  const { value: tagValues } = meta;
  const { isSubmitting, setFieldTouched } = useFormikContext();

  const tagInputRef = useRef<HTMLInputElement>(null);

  const clearTagInput = () => {
    if (tagInputRef.current === null) return;

    tagInputRef.current.dataset.idx = "-1";
    tagInputRef.current.dataset.id = '0';
    tagInputRef.current.dataset.name = "";
    tagInputRef.current.dataset.mode = "add";
    tagInputRef.current.value = "";
  }

  const resetTagInput = (replaceFunc: Function, removeFunc: Function) => {
    if (tagInputRef.current === null) return;

    const { idx, mode, id: idStr, name } = tagInputRef.current.dataset;

    if (idx !== undefined) {
      if (mode === 'edit' && name) {
        // revert values
        replaceFunc(parseInt(idx), {id: parseInt(`${idStr ? idStr : 0}`), name})
      } else if (mode === 'add' && idx !== '-1') {
        // remove added item
        removeFunc(parseInt(idx));
      }
    }
    clearTagInput();
  }

  const onChangeTagInput = (replaceFunc: Function, removeFunc: Function, pushFunc: Function) => {

      if (tagInputRef.current === null) return;

      setFieldTouched(fieldName, true);

      const value = tagInputRef.current.value;
      if (! value ) {
        resetTagInput(replaceFunc, removeFunc);
        return;
      }

      const { idx, mode, id: idStr } = tagInputRef.current.dataset;
      if (mode === 'add' && idx === '-1') {
        tagInputRef.current.dataset.idx = tagValues ? tagValues.length : 0;
        tagInputRef.current.dataset.id = '0';
        pushFunc({ id: 0, name: value});
      } else if (idx) {
        replaceFunc(parseInt(idx), {id: parseInt(`${idStr ? idStr : 0}`), name: value})
      }
  }

  const onClickTagBtn = (e: any) => {
    const elem = e.target.closest('.tagBtn');
    if (! (elem && tagInputRef.current && elem.dataset.idx)) return;

    const idx = parseInt(elem.dataset.idx);

    if (tagValues && idx < tagValues.length) {
      // load element values to tagInput
      tagInputRef.current.dataset.mode= 'edit';
      tagInputRef.current.dataset.idx = `${idx}`;
      tagInputRef.current.dataset.idx = `${tagValues[idx].id}`;
      tagInputRef.current.dataset.name = tagValues[idx].name;
      tagInputRef.current.value = tagValues[idx].name;
    }
  }

	return (
	<FieldArray
		name={fieldName}
		render={ helpers => (
    <div className={`col-${size}`}>
      <label className={clsx('fw-bold fs-7', { 'required': required === true})}>{label} :</label>
      {(tagValues && tagValues.length > 0) &&
      <span className='fs-8 text-gray-400 ps-2'>({tagValues.length})</span>
      }
      { (subLabel && typeof(subLabel) === 'string') &&
      <div className='fs-8 text-gray-400 ps-0.5'>
        {subLabel}
      </div>
      }
      <input
        ref={tagInputRef}
        className="form-control form-control-solid mb-1 py-2 w-50"
        data-idx="-1"
        data-mode="add"
        data-id="0"
        data-name=""
        onChange={() => {
          onChangeTagInput(helpers.replace, helpers.remove, helpers.push);
        }}
        onKeyDown={(e) => {
          // abort on Escape
          if (e && e.key) {
            if (e.key === 'Escape') {
              e.preventDefault();
              resetTagInput(helpers.replace, helpers.remove);
            } else if (e.key === 'Enter') {
              e.preventDefault();
              clearTagInput();
            }
          }
        }}
        disabled={isSubmitting}
      />
      {(tagValues && tagValues.length > 0) &&
      <div className='d-flex flex-row flex-wrap gap-2 form-control p-1 min-h-30px'>
      {tagValues.map( (obj: any, idx: number) => (
        <button
          key={`tagBtn${idx}`}
          type="button"
          className="tagBtn btn btn-sm btn-light text-dark btn-active-light-primary ps-3 pe-0 py-1"
          onClick={onClickTagBtn}
          title="Click to Select"
          data-idx={idx}
          data-name={obj.name}
          disabled={isSubmitting}
        >
          {obj.name}
          <div
            className="badge badge-sm badge-square text-hover-danger ps-2"
            title="Remove"
            onClick={(e: any) => {
              const elem = e.target.closest('.tagBtn');
              if (elem && window.confirm(`Are you sure you want to remove '${elem.dataset.name}'?`)) {
                helpers.remove(parseInt(elem.dataset.idx));
                clearTagInput();
                e.stopPropagation();
              }
            }}
          >
            <KTSVG path='/media/icons/duotune/arrows/arr011.svg' className='svg-icon' />
          </div>
        </button>
        ))}
      </div>
      }
      {meta.error && (
      <div className='fv-plugins-message-container'>
        <div className='fv-help-block fs-7'>
          <span role='alert'>{meta.error}</span>
        </div>
      </div>
      )}
    </div>
    )}
	/>
	)
}

const FormikErrorMessage = ({name} :{ name: string}) => {
  const [,meta] = useField(name);

  const { error } = meta;

  const displayError = useMemo(() => {
    if (! error) return '';

    if (! Array.isArray(error)) return error;

    let errors: Array<any> = [];

    error.forEach( (elem:any) => {
      if (typeof elem === 'string') {
        errors.push(elem);
        return;
      }
      if (typeof elem === 'object') {
        Object.values(elem).forEach((val: any) => {
          if (typeof val === 'string') {
            errors.push(`${val}`);
          }
        });
      }
    });

    const uniqueItems = uniq(errors);

    uniqueItems.sort();

    return uniqueItems.join('. ');

  }, [error])
  
  if (! (meta && meta?.touched && meta?.error)) {
    return null;
  }

  return (
  <div className='fv-plugins-message-container'>
    <div className='fv-help-block' >
      <div role="alert" className="text-danger fs-7" style={{wordBreak: 'break-all'}}>
        {displayError}.
      </div>            
    </div>
  </div>
  )
}

export {
  FormikTextInput, FormikTextField,
  FormikSwitch,
  FormikRadio,
  FormikCheckBox,
  FormikSelect,
  FormikFileUpload,
  FormikMultiFileUpload,
  FormikDateSelector,
  FormikTagInput,
  FormikErrorMessage,
}